<template>
  <v-col class="d-flex" cols="12" lg="4" md="4" sm="6" xl="3">
    <v-card class="white align-stretch d-flex" style="width: 100%">
      <v-list-item class="align-stretch d-flex justify-space-between px-0">
        <v-list-item-content class="align-content-space-between pt-1" style="height: 100%">
          <v-col class="text-overline mb-4" style="line-height: 16px;">
            {{ title }}
            <v-tooltip v-if="description" :open-on-click="$vuetify.breakpoint.smAndDown"
                       :open-on-hover="$vuetify.breakpoint.mdAndUp" top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn class="mt-n1" icon v-bind="attrs" x-small v-on="on">
                  <v-icon color="primary" size="18">mdi mdi-help-circle</v-icon>
                </v-btn>
              </template>
              <div class="tooltip-text">{{ description }}</div>
            </v-tooltip>
          </v-col>
          <v-list-item-subtitle :class="{ 'text-center': true }" class="subtitle-1 font-weight-black">
            <v-icon v-if="icon" :color="color" class="ml-n2 mt-n1 mr-2" size="20">{{ icon }}</v-icon>
            <span :class="textColor">{{ value }}</span>
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-card>
  </v-col>
</template>

<script>
import {FINANCIAL_DIR} from '@/plugins/constants';
import numeral from 'numeral';
import snakeCase from 'lodash/snakeCase';

export default {
  name: 'FinancialCard',
  data() {
    return {
      showTooltip: false,
    };
  },
  props: {
    financial: {
      type: Object,
      required: false,
    },
    name: {
      type: String,
      required: true,
    },
    valueType: {
      type: String,
      required: false,
      default: 'currency',
    },
    previousFinancial: {
      type: Object,
      required: false,
    },
    inverted: {
      type: Boolean,
      required: false,
      default: false,
    },
    grade: {
      type: Array,
      required: false,
    },
  },
  computed: {
    title() {
      return this.$te(`financials.values.${snakeCase(this.name)}`) ? this.$t(`financials.values.${snakeCase(this.name)}`) : this.name;
    },
    arrowType() {
      if (this.previousFinancial) {
        if (!this.financial || this.financial[this.name] === undefined || this.financial[this.name] === null) return null;
        if (!this.previousFinancial || this.previousFinancial[this.name] === undefined || this.previousFinancial[this.name] === null)
          return null;

        if (this.financial[this.name] > this.previousFinancial[this.name]) {
          if (this.inverted) return FINANCIAL_DIR.DOWN;
          return FINANCIAL_DIR.UP;
        }
        if (this.financial[this.name] < this.previousFinancial[this.name]) {
          if (this.inverted) return FINANCIAL_DIR.UP;
          return FINANCIAL_DIR.DOWN;
        }
        if (this.financial[this.name] === this.previousFinancial[this.name]) {
          return FINANCIAL_DIR.RIGHT;
        }
      }
      return '';
    },
    icon() {
      if (this.value === 'N/A') return '';
      switch (this.arrowType) {
        case FINANCIAL_DIR.RIGHT:
          return 'mdi mdi-arrow-right-bold';
        case FINANCIAL_DIR.UP:
          return 'mdi mdi-arrow-up-bold';
        case FINANCIAL_DIR.DOWN:
          return 'mdi mdi-arrow-down-bold';
        default:
          return '';
      }
    },
    color() {
      switch (this.arrowType) {
        case FINANCIAL_DIR.RIGHT:
          return 'warning';
        case FINANCIAL_DIR.UP:
          return 'success';
        case FINANCIAL_DIR.DOWN:
          return 'error';
        default:
          return '';
      }
    },
    textColor() {
      if (this.grade && this.internalValue !== null) {
        const size = this.grade.length;
        let colors = [];
        if (size === 2) {
          colors = ['error--text', 'warning--text', 'success--text'];
        } else if (size === 3) {
          colors = ['error--text', 'warning--text', 'success--text text--lighten-2', 'success--text'];
        } else if (size === 4) {
          colors = ['error--text', 'warning--text', 'warning--text text--lighten-2', 'success--text text--lighten-2', 'success--text'];
        }
        for (let i = 0; i < this.grade.length; i++) {
          if (this.internalValue < this.grade[i]) {
            return colors[i];
          }
        }
        for (let i = this.grade.length; i >= 0; i--) {
          if (this.internalValue >= this.grade[i]) {
            return colors[i + 1];
          }
        }
      }
      return '';
    },
    currency() {
      switch (this.financial['currency']) {
        case 'USD':
          return '$';
        case 'EUR':
          return '€';
      }
      return '';
    },
    internalValue() {
      if (!this.financial || this.financial[this.name] === undefined || this.financial[this.name] === null) return null;
      return this.financial[this.name];
    },
    value() {
      if (!this.financial || this.financial[this.name] === undefined || this.financial[this.name] === null) return 'N/A';

      if (this.valueType === 'percentage') {
        return numeral(this.internalValue).format('0%');
      }
      if (this.valueType === 'none') {
        return numeral(this.internalValue).format('0,0[.]00');
      }
      return this.currency + ' ' + numeral(this.internalValue).format('0,0[.]00');
    },
    description() {
      return this.$te(`financials.descriptions.${snakeCase(this.name)}`)
        ? this.$t(`financials.descriptions.${snakeCase(this.name)}`)
        : null;
    },
  },
};
</script>

<style lang="scss" scoped>
.tooltip-text {
  max-width: calc(25vw - 42px);
  @media #{map-get($display-breakpoints,'xl-only')} {
    max-width: calc(25vw - 42px);
  }
  @media #{map-get($display-breakpoints,'md-and-down')} {
    max-width: calc(50vw - 42px);
  }
  @media #{map-get($display-breakpoints,'sm-and-down')} {
    max-width: calc(75vw - 42px);
  }
  @media #{map-get($display-breakpoints,'xs-only')} {
    max-width: calc(100vw - 42px);
  }
}
</style>
